import { useEffect, useRef, useState } from "react";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "../../lib/data";

const links = [
  { href: "#services", label: "Specialities" },
  { href: "#about", label: "About" },
  { href: "#testimonials", label: "Reviews" },
  { href: "#instagram", label: "Instagram" },
  { href: "#faq", label: "FAQ" },
  { href: "#visit", label: "Visit" },
  { href: "/connect", label: "Connect", external: true },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");
  const [indicator, setIndicator] = useState({ left: 0, width: 0, opacity: 0 });
  const navRef = useRef(null);
  const linkRefs = useRef({});

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track which section is in view to drive the underline indicator
  useEffect(() => {
    const sectionIds = links.map((l) => l.href.slice(1));
    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!sections.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(`#${e.target.id}`);
        });
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
    );
    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);

  // Move underline indicator to active link
  useEffect(() => {
    const link = linkRefs.current[active];
    const nav = navRef.current;
    if (!link || !nav) {
      setIndicator((i) => ({ ...i, opacity: 0 }));
      return;
    }
    const navBox = nav.getBoundingClientRect();
    const linkBox = link.getBoundingClientRect();
    setIndicator({
      left: linkBox.left - navBox.left,
      width: linkBox.width,
      opacity: 1,
    });
  }, [active]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-bone/70 backdrop-blur-xl border-b border-line shadow-[0_2px_20px_-8px_rgba(14,156,169,0.18)]"
          : "bg-bone/40 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 h-20 flex items-center justify-between">
        <a href="#top" data-testid="navbar-logo" className="flex items-center gap-2 shrink-0">
          <img
            src={LOGO_URL}
            alt="Dr. Venkat Subramaniam — Best Urologist in Chennai"
            className="h-14 sm:h-16 w-auto logo-knockout"
          />
        </a>

        <nav ref={navRef} className="hidden lg:flex items-center gap-8 relative">
          {links.map((l) => (
            <a
              key={l.href}
              ref={(el) => (linkRefs.current[l.href] = el)}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`text-sm transition-colors ${
                active === l.href ? "text-ink font-semibold" : "text-ink-soft hover:text-ink"
              }`}
            >
              {l.label}
            </a>
          ))}
          <span
            className="nav-indicator"
            style={{
              left: `${indicator.left}px`,
              width: `${indicator.width}px`,
              opacity: indicator.opacity,
            }}
          />
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#book"
            data-testid="navbar-book-btn"
            className="hidden md:inline-flex btn-primary px-6 py-3 rounded-full text-sm font-medium tracking-wide"
          >
            Book Consultation
          </a>
          <button
            aria-label="Open menu"
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-ink p-2"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-bone border-t border-line">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-base text-ink-soft hover:text-ink"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#book"
              onClick={() => setOpen(false)}
              data-testid="navbar-mobile-book-btn"
              className="btn-primary px-6 py-3 rounded-full text-sm font-medium tracking-wide text-center"
            >
              Book Consultation
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
